import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
    Box,
    FormControl,
    FormLabel,
    Typography,
    OutlinedInput,
    InputAdornment,
    TextField,
    Checkbox,
    FormControlLabel,
    CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Slider from "@mui/material/Slider";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputMask from "react-input-mask";
import { get, post } from "../../api";

import "./style.css";

const Input = forwardRef((props, ref) => {
    let mask = "dD-mM-YYYY";
    let formatChars = {
        Y: "[0-9]",
        m: "[0-1]",
        M: "[0-9]",
        d: "[0-3]",
        D: "[1-9]",
        y: "[1-2]",
    };

    const convertToDate = (date) => {
        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        // Get the components of the date
        const month = date.getUTCMonth() + 1; // Months are zero-based, so add 1
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();

        // Create the MM/DD/YYYY formatted string
        const formattedDate = `${month.toString().padStart(2, "0")} / ${day.toString().padStart(2, "0")} / ${year}`;

        return formattedDate;
    };

    const convertToUTC = (date) => {
        const utcDate = new Date(date);
        utcDate.setMinutes(utcDate.getMinutes() - utcDate.getTimezoneOffset());
        return utcDate;
    };

    const parseDate = (MMDDYYYY) => {
        // Split the input string into components
        const [month, day, year] = MMDDYYYY.split("/");
        let localDate = new Date(year, month - 1, day);
        const utcDate = convertToUTC(localDate);
        return utcDate;
    };

    const [inputValue, setInputValue] = useState(
        props.value ? (props.type === "date" ? convertToDate(props.value) : props.value) : ""
    );
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const emailRegex = /^[a-zA-Z0-9+._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    const dateRegex = /^(0[1-9]|1[012])( \/ )(0[1-9]|[12][0-9]|3[01])( \/ )(19|20)\d\d$/;

    const validateInput = (type, newValue) => {
        if (type === "email") {
            return emailRegex?.test(newValue);
        } else if (type === "date") {
            return dateRegex?.test(newValue);
        } else if (type === "maskType" || type === "phone") {
            return newValue?.trim().length === props.mask?.length;
        }
        return true;
    };

    const handleChange = (value) => {
        let newValue = value;
        setErrorMessage("");
        setInputValue(newValue);
        props.onChange(props.type === "date" ? parseDate(newValue) : newValue);
        props.isRequired
            ? props.type === "email" || props.type === "date" || props.type === "maskType" || props.type === "phone"
                ? validateInput(props.type, newValue)
                    ? setHasError(false)
                    : setHasError(true)
                : newValue
                ? setHasError(false)
                : setHasError(true)
            : setHasError(false);
        if (props.type === "email") {
            handleVerifyEmail(value);
        }
        if (props.type === "phone") {
            handleVerifyPhone(value);
        }
        return;
    };

    useImperativeHandle(ref, () => ({
        validate,
    }));

    const validate = () => {
        let valid = false;
        valid = props?.isRequired ? (inputValue ? true : false) : true;
        let newErrorMessage = "";
        if (valid && props.type === "email") {
            valid = emailRegex.test(inputValue) ? true : false;
            if (!valid) {
                newErrorMessage = "Invalid email.";
            }
            if (valid) {
                if (props.existingEmail?.indexOf(inputValue) > -1) {
                    newErrorMessage = "This email address is already in use.";
                    valid = false;
                }
            }
            if (newErrorMessage) {
                setErrorMessage(errorMessage ? errorMessage : newErrorMessage);
            } else {
                setErrorMessage(errorMessage?.startsWith("This co-applicant") ? errorMessage : newErrorMessage);
            }
        } else if (valid && props.type === "phone") {
            valid = inputValue?.trim().length === props.mask?.length ? true : false;
            if (!valid) {
                newErrorMessage = "Invalid phone.";
            }
            if (valid) {
                if (props.existingPhones?.indexOf(inputValue) > -1) {
                    newErrorMessage = "This phone is already used.";
                    valid = false;
                }
            }
            if (newErrorMessage) {
                setErrorMessage(errorMessage ? errorMessage : newErrorMessage);
            } else {
                setErrorMessage(errorMessage?.startsWith("This co-applicant") ? errorMessage : newErrorMessage);
            }
        } else if (valid && props.type === "date") {
            valid = dateRegex.test(inputValue) ? true : false;
        } else if (valid && props.type === "maskType") {
            valid = inputValue?.trim().length === props.mask?.length;
        }
        if (valid && props.type === "email" && props.verifyEmail) {
            valid = errorMessage?.startsWith("This co-applicant") ? false : newErrorMessage ? false : true;
        }
        if (valid && props.type === "phone" && props.verifyPhone) {
            valid = errorMessage?.startsWith("This co-applicant") ? false : newErrorMessage ? false : true;
        }

        setHasError(!valid);
        if (loading) {
            return false;
        }
        return valid;
    };

    const removeValidate = (event) => {
        setHasError(false);
    };

    const numberFormatter = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    });

    const handleVerifyEmail = async (value) => {
        if (props.type === "email" && value && emailRegex.test(value) && props.verifyEmail) {
            setLoading(true);
            let result = await post("applicant/verifyEmail", {
                emailId: value,
                applicationId: props.applicationId,
            });
            console.log(result);
            setLoading(false);
            if (result.isSuccess && result.data) {
                setHasError(true);
                setErrorMessage(
                    errorMessage
                        ? errorMessage
                        : "This co-applicant (or guarantor) already has an open application and cannot be invited. Please reach out to our team at 833-641-0677 to have their application merged. In the meantime, please continue your application without adding them."
                );
                return false;
            }
            return true;
        }
    };

    const handleVerifyPhone = async (value) => {
        if (props.type === "phone" && value && value?.trim().length === props.mask?.length && props.verifyPhone) {
            setLoading(true);
            let result = await post("applicant/verifyPhone", {
                phone: value,
                applicationId: props.applicationId,
            });
            console.log(result);
            setLoading(false);
            if (result.isSuccess && result.data) {
                setHasError(true);
                setErrorMessage(
                    "This co-applicant (or guarantor) already has an open application and cannot be invited. Please reach out to our team at 833-641-0677 to have their application merged. In the meantime, please continue your application without adding them."
                );
                return false;
            }
            return true;
        }
    };

    const getFieldValue = () => {
        return (
            <>
                {inputValue && (
                    <Box>
                        {props.prefix ? <span style={{ marginRight: "5px" }}>{props.prefix}</span> : ""}
                        {props.type === "number" ? numberFormatter.format(inputValue) : inputValue}
                    </Box>
                )}
            </>
        );
    };

    const getField = () => {
        if (props.type === "date") {
            return (
                <>
                    <InputMask
                        mask={props.mask}
                        value={inputValue}
                        disabled={props.disabled}
                        maskChar=" "
                        required
                        onChange={(event) => handleChange(event.target.value)}>
                        {() => <TextField placeholder={props.placeholder} />}
                    </InputMask>
                    {/* <LocalizationProvider fullWidth dateAdapter={AdapterDayjs}>
                        <DatePicker
                            inputFormat="MM / DD / YYYY"
                            value={props.value ? props.value : null}
                            valueDefault={null}
                            emptyLabel="custom label"
                            margin={props.margin}
                            onChange={(newValue) => {
                                handleChange(newValue);
                            }}
                            renderInput={(props) => (
                                <TextField
                                    {...props}
                                    error={hasError}
                                    inputProps={{
                                        ...props.inputProps,
                                        placeholder: "MM / DD / YYYY",
                                    }}
                                    fullWidth
                                />
                            )}
                        />
                    </LocalizationProvider> */}
                </>
            );
        } else if (props.type === "checkbox") {
            return (
                <FormControlLabel
                    sx={{ display: "flex", alignItems: "start" }}
                    label={
                        <Typography sx={{ mt: 1 }} variant="body1">
                            {props.label}
                        </Typography>
                    }
                    control={
                        <Checkbox
                            disabled={props.disabled}
                            label={props.label}
                            id="checkbox"
                            checked={inputValue}
                            onChange={(event) => handleChange(event.target.checked)}
                            sx={{ color: hasError ? "#FF0000" : "#00000" }}
                        />
                    }
                />
            );
        } else if (props.type === "slider") {
            return (
                <Box
                    sx={{
                        p: 1,
                        height: 80,

                        borderRadius: "8px",
                        border: "1px solid #E6E8F0",

                        mt: 1,
                        backgroundColor: "#ffffff",
                        overflow: "visible",
                    }}>
                    <FormControlLabel
                        sx={{ display: "flex", alignItems: "start" }}
                        control={
                            <Slider
                                value={props.value}
                                onChange={(event) => handleChange(event.target.value)}
                                min={0}
                                max={props.maxValue}
                            />
                        }
                        label={
                            <Grid
                                sx={{ width: "100%" }}
                                container
                                justifyContent={"space-between"}
                                alignItems={"center"}>
                                <Grid item>
                                    <Typography variant="subtitle1">
                                        <b>{props.label}</b>
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography variant="subtitle1">
                                        <Box
                                            sx={{
                                                border: "1px solid #E6E8F0",
                                                borderRadius: "8px",
                                                px: 1,
                                                alignItems: "end",
                                            }}>
                                            {props.prefix}
                                            {props.value}
                                            {props.postfix}
                                        </Box>
                                    </Typography>
                                </Grid>
                            </Grid>
                        }
                        labelPlacement="top"
                    />
                </Box>
            );
        } else if (props.type === "radio") {
            return (
                <RadioGroup
                    name="radio-buttons-group"
                    onChange={(event) => handleChange(event.target.value)}
                    value={inputValue}
                    aria-label="radio-group">
                    {props.label && (
                        <Typography
                            gutterBottom
                            variant="subtitle1"
                            sx={{ mt: "10px", mb: "0px", color: hasError ? "#FF0000" : "" }}>
                            {props.label}
                        </Typography>
                    )}
                    {props.options.map((column) => (
                        <Box
                            component="span"
                            sx={{
                                px: 1,
                                borderRadius: "10px",
                                border: "1px solid",
                                borderColor: hasError ? "#FF0000" : column.value === inputValue ? "#06aed4" : "#E6E8F0",
                                mt: 1,
                                backgroundColor: column.value === inputValue ? "#E6F6FA" : "#ffffff",
                            }}>
                            <FormControlLabel
                                sx={{ display: "flex", alignItems: "start" }}
                                value={column.value}
                                control={<Radio />}
                                label={
                                    <Typography sx={{ mt: "10px" }} variant="subtitle1">
                                        {column.label}
                                        <span
                                            style={{
                                                color: column.value === inputValue ? "#000000" : "#90a4ae",
                                                fontSize: ".75rem",
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                            }}>
                                            {column.subheading ? " " + column.subheading : ""}
                                        </span>
                                    </Typography>
                                }
                            />
                            {column.value === inputValue && column.componentOnSelect}
                        </Box>
                    ))}
                </RadioGroup>
            );
        } else if (props.type === "select") {
            return (
                <Select
                    id={props.id}
                    value={inputValue}
                    margin={props.margin}
                    onChange={(event) => handleChange(event.target.value)}
                    error={hasError}
                    displayEmpty={props.displayEmpty ? true : false}
                    disabled={props.disabled}>
                    <MenuItem value="" sx={{ color: "gray" }}>
                        Select
                    </MenuItem>
                    {props.options.map((item) => (
                        <MenuItem key={item.id} value={item.value}>
                            {item.label ? item.label : item.value}
                        </MenuItem>
                    ))}
                </Select>
            );
        } else if (props.type === "maskType") {
            return (
                <InputMask
                    mask={props.mask}
                    value={inputValue}
                    disabled={props.disabled}
                    maskChar=" "
                    required
                    onChange={(event) => handleChange(event.target.value)}>
                    {() => <TextField placeholder={props.placeholder} />}
                </InputMask>
            );
        } else if (props.type === "phone") {
            return (
                <Box sx={{ position: "relative" }}>
                    <InputMask
                        mask={props.mask}
                        value={inputValue}
                        disabled={props.disabled}
                        maskChar=" "
                        required
                        onBlur={() => {
                            validate();
                        }}
                        onFocus={removeValidate}
                        onChange={(event) => handleChange(event.target.value)}>
                        {() => <TextField style={{ width: "100%" }} placeholder={props.placeholder} />}
                    </InputMask>
                    {loading && (
                        <InputAdornment sx={{ position: "absolute", top: "28px", right: "14px" }} position="end">
                            <CircularProgress size={20} />
                        </InputAdornment>
                    )}
                </Box>
            );
        } else {
            return (
                <OutlinedInput
                    {...props.rest}
                    id={props.id}
                    type={props.type ? props.type : "text"}
                    value={inputValue}
                    autoFocus={props.isAutoFocus}
                    onChange={(event) => {
                        handleChange(event.target.value);
                    }}
                    onBlur={() => {
                        validate();
                    }}
                    onFocus={removeValidate}
                    margin={props.margin}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    startAdornment={
                        props.prefix ? <InputAdornment position="start">{props.prefix}</InputAdornment> : ""
                    }
                    endAdornment={
                        loading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : (
                            ""
                        )
                    }
                    inputProps={{ maxLength: props.maxLength ? props.maxLength : 255 }}
                    error={hasError}
                />
            );
        }
    };

    return (
        <Box className="Input" sx={{ display: "flex", mt: props.margin === "normal" ? "10px" : "0px" }}>
            <FormControl {...props.rest} fullWidth variant="outlined" className={hasError ? "has-error" : ""}>
                {props.type !== "checkbox" && props.type !== "slider" && props.type !== "radio" && (
                    <FormLabel htmlFor="my-input">
                        <Typography
                            gutterBottom
                            variant="subtitle1"
                            sx={{
                                color: hasError ? "#FF0000" : props.labelColor ? props.labelColor : "",
                                fontSize: props.labelFontSize ? props.labelFontSize : "inherit",
                            }}>
                            {props.label}
                        </Typography>
                    </FormLabel>
                )}
                {props.readonly ? getFieldValue() : getField()}

                {errorMessage ? (
                    <Typography
                        gutterBottom
                        variant="subtitle1"
                        sx={{ fontSize: "12px", color: hasError ? "#FF0000" : "" }}>
                        {errorMessage}
                    </Typography>
                ) : (
                    ""
                )}
            </FormControl>
        </Box>
    );
});

export { Input };
